import React, { useEffect } from 'react'
import { ArrowLeft, Shield, Lock, Eye, Database, Globe, Trash2 } from 'lucide-react'
import { Button } from './ui/button'

export function PrivacyPolicy({ navigate }) {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-primary/10 font-sans">
      <div className="max-w-4xl mx-auto px-6 py-12 md:py-20">
        <div className="mb-12">
          <Button
            variant="ghost"
            onClick={() => {
              navigate('/')
            }}
            className="group -ml-3 px-3 rounded-full hover:bg-muted/50 transition-all active:scale-95 flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
            <span className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors">
              Back to App
            </span>
          </Button>
        </div>

        <article className="prose prose-sm md:prose-base dark:prose-invert max-w-none space-y-8">
          <header className="space-y-4 border-b border-border/50 pb-8">
            <h1 className="text-4xl font-black tracking-tight md:text-5xl">Privacy Policy</h1>
            <p className="text-muted-foreground font-medium">Last updated: March 8, 2026</p>
          </header>

          <section className="space-y-4">
            <p className="leading-relaxed text-muted-foreground/80">
              This Privacy Policy explains how <strong className="text-foreground">ZOY</strong>{' '}
              ("ZOY", "we", "us", or "our") collects, uses, stores, and processes information when
              you access or use <strong className="text-foreground">https://zoy.io</strong> and any
              related applications, software, or services (collectively, the{' '}
              <strong className="text-foreground">"Services"</strong>).
            </p>
            <p className="leading-relaxed text-muted-foreground/80 font-medium">
              By accessing or using the Services, you agree to the collection, use, and processing
              of information as described in this Privacy Policy.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 py-8 border-y border-border/30">
            <div className="p-4 rounded-xl bg-secondary/20 border border-border/20 space-y-2">
              <Shield className="h-5 w-5 text-primary" />
              <h3 className="font-bold text-sm">No Sale of Data</h3>
              <p className="text-xs text-muted-foreground/70">
                We do not sell, rent, or trade your personal information.
              </p>
            </div>
            <div className="p-4 rounded-xl bg-secondary/20 border border-border/20 space-y-2">
              <Lock className="h-5 w-5 text-primary" />
              <h3 className="font-bold text-sm">Security First</h3>
              <p className="text-xs text-muted-foreground/70">
                We use commercially reasonable measures to protect your data.
              </p>
            </div>
            <div className="p-4 rounded-xl bg-secondary/20 border border-border/20 space-y-2">
              <Eye className="h-5 w-5 text-primary" />
              <h3 className="font-bold text-sm">Telemetry</h3>
              <p className="text-xs text-muted-foreground/70">
                Technical data helps us improve performance and reliability.
              </p>
            </div>
          </div>

          <section id="info-collect" className="space-y-4 scroll-mt-20">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              1. Information We Collect
            </h2>
            <div className="space-y-4">
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-foreground/90">Personal Information</h3>
                <ul className="list-disc list-inside text-muted-foreground/80 space-y-1 ml-2 text-sm">
                  <li>Name and Email address</li>
                  <li>Billing and Payment information</li>
                  <li>Account credentials</li>
                  <li>Communications sent to us</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-foreground/90">Usage Data</h3>
                <p className="text-sm text-muted-foreground/80">
                  Automatically collected via system logs and analytics tools:
                </p>
                <ul className="list-disc list-inside text-muted-foreground/80 space-y-1 ml-2 text-sm">
                  <li>IP address and Device identifiers</li>
                  <li>Browser type and Operating system</li>
                  <li>Interaction events and Feature usage</li>
                  <li>Error logs and Performance metrics</li>
                </ul>
              </div>
            </div>
          </section>

          <section id="how-use" className="space-y-4 scroll-mt-20">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              2. How We Use Information
            </h2>
            <p className="leading-relaxed text-muted-foreground/80">
              We use collected information to operate, maintain, and improve the Services. This
              includes troubleshooting errors, preventing fraud, analyzing usage patterns, and
              providing user support.
            </p>
          </section>

          <section id="no-sale" className="space-y-4 scroll-mt-20">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              4. Data Use and Non-Sale of Data
            </h2>
            <div className="bg-primary/5 rounded-xl p-6 border border-primary/20">
              <p className="leading-relaxed text-foreground font-semibold italic">
                ZOY does not sell, rent, or trade personal information to third parties.
              </p>
              <p className="mt-2 text-sm text-muted-foreground/80">
                Information is primarily used to operate, secure, and improve the platform. We do
                not sell data to advertising networks or data brokers.
              </p>
            </div>
          </section>

          <section id="security" className="space-y-4 scroll-mt-20">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              9. Data Security
            </h2>
            <p className="leading-relaxed text-muted-foreground/80">
              We implement commercially reasonable security measures. However, no system or internet
              transmission can be guaranteed to be 100% secure. You are responsible for maintaining
              the confidentiality of your credentials.
            </p>
          </section>

          <section id="liability" className="space-y-4 scroll-mt-20">
            <h2 className="text-2xl font-bold flex items-center gap-2 text-destructive/80">
              <span className="h-1.5 w-1.5 rounded-full bg-destructive/50" />
              15. Limitation of Liability
            </h2>
            <div className="bg-destructive/5 p-4 border-l-4 border-destructive/20 rounded-r-lg space-y-2">
              <p className="text-sm font-bold text-destructive/80 uppercase tracking-wider">
                Crucial Notice
              </p>
              <p className="text-sm leading-relaxed text-muted-foreground/80 italic">
                To the maximum extent permitted by law, ZOY shall have no financial liability
                arising from this Privacy Policy or the processing of information. If liability
                cannot be excluded, it shall not exceed $100 USD.
              </p>
            </div>
          </section>

          <footer className="pt-20 border-t border-border/50 flex flex-col items-center gap-4">
            <div className="flex flex-col items-center gap-1 text-center">
              <p className="text-sm font-bold text-foreground">Questions?</p>
              <p className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors cursor-pointer">
                help@zoy.io
              </p>
            </div>
            <p className="text-[10px] font-medium text-muted-foreground/50 uppercase tracking-widest mt-4">
              © 2026 ZOY. All rights reserved.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                navigate('/')
              }}
              className="rounded-full px-8 mt-4"
            >
              Back to App
            </Button>
          </footer>
        </article>
      </div>
    </div>
  )
}
